/**
 * THE APP — who is signed in, and which screen that lets them see.
 *
 * SIGN-IN IS A STATE, NOT A PLACE. An unauthenticated visitor sees the
 * sign-in screen where they stand: the address bar keeps the route they asked
 * for, so signing in renders exactly that route rather than dropping them on a
 * landing page and asking them to find their way back. There is no redirect to
 * unwind and no "return to" parameter to lose.
 *
 * A 401 ANYWHERE MEANS THE SAME THING. The transport reports it once (`whenSignedOut`)
 * and the session is forgotten; the address is untouched, so the way back is
 * the same way in.
 *
 * NOTHING PROFESSIONAL IS HELD HERE. The principal is who you are, not what
 * the work says; every screen below reads its own state from the server and
 * re-reads it after every act.
 */
import { useEffect, useState } from "react";
import { Route, Routes } from "react-router-dom";
import { MeResponse, type Principal } from "@leveri/contracts";
import { Reading } from "../file/index.js";
import { call, whenSignedOut } from "../api.js";
import { BoardScreen } from "./board.js";
import { ClientScreen } from "./client.js";
import { ClientsScreen } from "./clients.js";
import { EngagementScreen } from "./engagement.js";
import { SignIn } from "./signIn.js";
import { NotHere, PrincipalContext, TopBar } from "./shell.js";
import { WorkFileScreen } from "./work/file.js";
import { FirmScreen } from "./firm.js";

export function App() {
  /** `undefined` while the session has not been asked about; `null` once it has and there is none. */
  const [principal, setPrincipal] = useState<Principal | null | undefined>(undefined);

  useEffect(() => {
    whenSignedOut(() => setPrincipal(null));
    call("GET", "/api/auth/me", MeResponse)
      .then((response) => setPrincipal(response.principal))
      .catch(() => setPrincipal(null));
    return () => whenSignedOut(null);
  }, []);

  if (principal === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Reading>Opening Leveri…</Reading>
      </div>
    );
  }
  if (principal === null) return <SignIn onSignedIn={setPrincipal} />;

  return (
    <PrincipalContext.Provider value={principal}>
      <TopBar principal={principal} onSignedOut={() => setPrincipal(null)} />
      <Routes>
        <Route path="/" element={<BoardScreen />} />
        <Route path="/clients" element={<ClientsScreen />} />
        <Route path="/clients/:clientId" element={<ClientScreen />} />
        {/* An engagement is a professional context, and now has an address (R5.1). */}
        <Route path="/engagements/:engagementId" element={<EngagementScreen />} />
        <Route path="/work/:workId" element={<WorkFileScreen />} />
        <Route path="/firm" element={<FirmScreen />} />
        <Route path="*" element={<NotHere />} />
      </Routes>
    </PrincipalContext.Provider>
  );
}
